import {
  API,
  DEMO_CLEAR_TAIL_SECONDS,
  DEMO_FILTER_CLEAR_SECONDS,
  DEMO_PRUNE_AFTER_SECONDS,
  DEMO_SCENARIOS,
  DemoScenarioId,
  DemoScenarioState,
  Hub,
  demoScenarioKey,
  graphql,
  parseDemoScenarios,
  useSensors,
} from "common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Separator, Spinner, Text, XStack, YStack } from "tamagui";

import { useShowToast } from "../../hooks/useShowToast";
import { formatDateTime } from "../../utils/formatDate";

type Scenarios = Record<string, DemoScenarioState>;

const nowSeconds = () => Math.floor(Date.now() / 1000);

/** A stopped scenario whose alert is still clearing (the feeder's clear tail). */
const isClearing = (key: string, state: DemoScenarioState | undefined, now: number) => {
  if (!state?.clearedAt) return false;
  const tail = key.endsWith(`/${DemoScenarioId.FilterClog}`)
    ? DEMO_FILTER_CLEAR_SECONDS
    : DEMO_CLEAR_TAIL_SECONDS;
  return now - state.clearedAt < tail;
};

/**
 * Start / stop bad-data scenarios on a demo home (common DEMO_HUB_IDS), opened
 * from DemoLauncher's hidden picker. The demoFeeder Lambda reads the scenarios from
 * Hub.config.demo every minute and feeds the bad readings; the real alert code
 * raises and clears the alerts from there.
 */
const DemoPanelDialog = ({ hub, onClose }: { hub: Hub; onClose: () => void }) => {
  const { t } = useTranslation();
  const { showErrorToast } = useShowToast();
  const { sensors, loading } = useSensors(hub.id);
  const [scenarios, setScenarios] = useState<Scenarios>(() => parseDemoScenarios(hub.config));
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(nowSeconds);

  useEffect(() => {
    const timer = setInterval(() => setNow(nowSeconds()), 15000);
    return () => clearInterval(timer);
  }, []);

  // The table row's config can be stale (another tab, or the list loaded long
  // ago); a running scenario must never read as "Start" and get its `since` reset.
  useEffect(() => {
    let cancelled = false;
    (API.graphql({ query: graphql.getHub, variables: { id: hub.id } }) as Promise<any>)
      .then((response) => {
        if (!cancelled) setScenarios(parseDemoScenarios(response?.data?.getHub?.config));
      })
      .catch((error: unknown) => console.error("Demo scenario load failed:", error));
    return () => {
      cancelled = true;
    };
  }, [hub.id]);

  // Read-modify-write on the freshest config so another tab's changes (or any
  // other config key) aren't overwritten with this dialog's stale copy.
  const writeScenarios = async (change: (next: Scenarios, at: number) => void) => {
    setSaving(true);
    try {
      const response: any = await API.graphql({ query: graphql.getHub, variables: { id: hub.id } });
      const config = JSON.parse(response?.data?.getHub?.config || "{}");
      const at = nowSeconds();
      const next: Scenarios = { ...(config.demo?.scenarios ?? {}) };
      for (const [key, state] of Object.entries(next)) {
        if (state?.clearedAt && at - state.clearedAt > DEMO_PRUNE_AFTER_SECONDS) delete next[key];
      }
      change(next, at);
      config.demo = { ...(config.demo ?? {}), scenarios: next };
      await API.graphql({
        query: graphql.updateHub,
        variables: { input: { id: hub.id, config: JSON.stringify(config) } },
      });
      setScenarios(next);
      setNow(at);
    } catch (error) {
      console.error("Demo scenario update failed:", error);
      showErrorToast(t("baseStations.demo.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const start = (key: string) =>
    writeScenarios((next, at) => {
      next[key] = { since: at, clearedAt: null };
    });
  const stop = (key: string) =>
    writeScenarios((next, at) => {
      if (next[key] && !next[key].clearedAt) next[key] = { ...next[key], clearedAt: at };
    });
  const stopAll = () =>
    writeScenarios((next, at) => {
      for (const [key, state] of Object.entries(next)) {
        if (!state.clearedAt) next[key] = { ...state, clearedAt: at };
      }
    });

  const anyRunning = Object.values(scenarios).some((state) => state && !state.clearedAt);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={640}
          maxWidth="95vw"
          maxHeight="90dvh"
          overflow="scroll"
          gap="$4"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          <Dialog.Title
            size="$15"
            fontSize="$9.5"
            fontWeight="bold"
            $sm={{ lineHeight: 36 }}
          >
            {t("baseStations.demo.title", { name: hub.name })}
          </Dialog.Title>
          <Text fontSize="$3" color="$gray11">
            {t("baseStations.demo.intro")}
          </Text>

          {loading ? (
            <Spinner />
          ) : (
            <YStack gap="$3">
              {sensors.map((sensor: any) => {
                const options = DEMO_SCENARIOS.filter((s) => s.sensorType === sensor.type);
                if (options.length === 0) return null;
                return (
                  <YStack key={sensor.id} gap="$2">
                    <Separator />
                    <Text fontWeight="bold">
                      {[sensor.name, sensor.location].filter(Boolean).join(" · ")}
                    </Text>
                    {options.map((scenario) => {
                      const key = demoScenarioKey(sensor.id, scenario.id);
                      const state = scenarios[key];
                      const running = !!state && !state.clearedAt;
                      const clearing = isClearing(key, state, now);
                      return (
                        <XStack key={key} alignItems="center" justifyContent="space-between" gap="$3">
                          <YStack flex={1} gap="$1">
                            <Text>{scenario.label}</Text>
                            <Text fontSize="$2" color="$gray10">
                              {running
                                ? t("baseStations.demo.runningSince", {
                                    at: formatDateTime(new Date(state.since * 1000)),
                                  })
                                : clearing
                                  ? t("baseStations.demo.clearing")
                                  : scenario.timing}
                            </Text>
                          </YStack>
                          <Button
                            size="$3"
                            disabled={saving}
                            theme={running ? "red" : undefined}
                            onPress={() => (running ? stop(key) : start(key))}
                          >
                            {running ? t("baseStations.demo.stop") : t("baseStations.demo.start")}
                          </Button>
                        </XStack>
                      );
                    })}
                  </YStack>
                );
              })}
            </YStack>
          )}

          <XStack justifyContent="flex-end" gap="$3">
            <Button disabled={saving || !anyRunning} onPress={stopAll}>
              {t("baseStations.demo.stopAll")}
            </Button>
            <Button onPress={onClose}>{t("common.close")}</Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default DemoPanelDialog;
